<script setup>
import {useBaseStore} from "~/stores/base";
import {useShare} from "~/composables/share";
import star10Percent from "assets/img/icon-star-10-percent.svg";
import star20Percent from "assets/img/icon-star-20-percent.svg";
import star30Percent from "assets/img/icon-star-30-percent.svg";
import star40Percent from "assets/img/icon-star-40-percent.svg";
import star50Percent from "assets/img/icon-star-50-percent.svg";
import star60Percent from "assets/img/icon-star-60-percent.svg";
import star70Percent from "assets/img/icon-star-70-percent.svg";
import star80Percent from "assets/img/icon-star-80-percent.svg";
import star90Percent from "assets/img/icon-star-90-percent.svg";
import star100Percent from "assets/img/icon-star-filled.svg";
import {shareGenerate} from "~/api/api.share";

const BNTOMessage = useNuxtApp().$BNTOMessage
const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(['introduce'])

/** props **/
const props = defineProps({})

/** data **/
const isVoteLocal = ref(false)
const isLoading = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const open = computed(() => useBaseStore().getterIsShowModalShare)
const shareData = computed(() => useBaseStore().getterShareData)
const productId = computed(() => route.params.slug[0])
const isVote = computed(() => useProductStore().getterIsVote)

const fullStars = computed(() => Math.floor(shareData.value.productInfo.reviewAverage || 0))
const hasHalfStar = computed(() => shareData.value.productInfo.reviewAverage % 1 >= 0.5)
const halfStarImg = computed(() => {
  const percent = Math.round(shareData.value.productInfo.reviewAverage % 1 * 10) * 10
  switch (percent) {
    case 10:
      return star10Percent
    case 20:
      return star20Percent
    case 30:
      return star30Percent
    case 40:
      return star40Percent
    case 50:
      return star50Percent
    case 60:
      return star60Percent
    case 70:
      return star70Percent
    case 80:
      return star80Percent
    case 90:
      return star90Percent
    case 100:
      return star100Percent
  }
})
const emptyStars = computed(() => 5 - fullStars.value - (hasHalfStar.value ? 1 : 0))

/** watch **/
watch(open, () => {
  // 为什么有这一步，是因为，在投票创建者访问投票结果窗口中可以再次分享
  // 此时的分享默认为勾选了投票的case
  if (open.value) isVoteLocal.value = isVote.value

  if (!open.value) {
    useProductStore().updateIsVote(false)
    isVoteLocal.value = false
  }
})
watch(isVoteLocal, async () => {
  if (open.value) {
    /** 解决BNTO-2537 切换是否投票时图片的错乱问题 **/
    useBaseStore().updateShareData({
      ...shareData.value,
      productInfo: {
        ...shareData.value.productInfo,
        mainImage: {
          ...shareData.value.productInfo.mainImage,
          url: ""
        }
      }
    })

    if (isVoteLocal.value) {
      isLoading.value = true
      try {
        const res = await shareGenerate({resourceId: productId.value, resourceType: "vote"})
        useBaseStore().updateShareData(res.result)
        isLoading.value = false
      } catch (e) {
        // 接口失败如登录态等，则结束loading、关闭弹窗
        isLoading.value = false
        handleClose()
      }
    } else {
      isLoading.value = true
      try {
        const res = await shareGenerate({resourceId: productId.value, resourceType: "product"})
        useBaseStore().updateShareData(res.result)
        isLoading.value = false
      } catch (e) {
        // 接口失败如登录态等，则结束loading、关闭弹窗
        isLoading.value = false
        handleClose()
      }
    }
  }
})

/** methods **/
const handleClose = () => {
  useBaseStore().updateIsShowModalShare(false)
}
const handleX = () => {
  useShare().x({
    shareDesc: shareData.value.shareInfo.x.shareDesc,
    shareLink: shareData.value.shareInfo.x.shareLink,
  })
}
const handleFacebook = () => {
  useShare().facebook({
    shareLink: shareData.value.shareInfo.facebook.shareLink,
  })
}
const handleInstagram = () => {
  useShare().instagram({})
}
const handlePinterest = () => {
  useShare().pinterest({
    shareLink: shareData.value.shareInfo.pinterest.shareLink,
    imageUrl: shareData.value.shareInfo.pinterest.shareImage.url,
    shareDesc: shareData.value.shareInfo.pinterest.shareDesc,
  })
}
const handleMessage = () => {
  useShare().message({
    shareDesc: shareData.value.shareInfo.messages.shareDesc,
    shareLink: shareData.value.shareInfo.messages.shareLink,
  })
}
const handleEmail = () => {
  useShare().email({
    shareTitle: shareData.value.shareInfo.mail.shareTitle,
    shareDesc: shareData.value.shareInfo.mail.shareDesc,
    shareLink: shareData.value.shareInfo.mail.shareLink,
  })
}
const handleDownloadImage = () => {
  useShare().downloadImage({
    imageUrl: shareData.value.shareInfo.download.shareImage.url
  })
}
const handleCopyLink = () => {
  useShare().copyLink({
    shareLink: shareData.value.shareInfo.copyLink.shareLink,
  })
}
const handleMore = () => {
  useShare().more({
    shareTitle: shareData.value.shareInfo.more.shareTitle,
    shareDesc: shareData.value.shareInfo.more.shareDesc,
    shareLink: shareData.value.shareInfo.more.shareLink
  })
}

/** lifecycle **/

</script>

<template>
  <div class="slide-product-share-mobile" v-if="isMobile">
    <div class="previewImage">
      <div class="top">
        <div class="top-left"><img v-if="shareData.productInfo.mainImage.url" :src="shareData.productInfo.mainImage.url" alt="img"></div>
        <div class="top-right" v-if="isVoteLocal && !isLoading">
          <img src="@/assets/img/icon-like.png" alt="icon">
          <img src="@/assets/img/icon-half-like.png" alt="icon">
          <img src="@/assets/img/icon-unlike.png" alt="icon">
        </div>
      </div>
      <div class="bottom">
        <div class="name">{{ shareData.productInfo.productName }}</div>
        <div class="rate">
          <div class="stars">
            <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in fullStars">
            <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
            <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in emptyStars">
          </div>
          <div class="points">{{ shareData.productInfo.reviewAverage }}</div>
        </div>
      </div>
    </div>
    <div class="checkbox-vote">
      <el-checkbox v-model="isVoteLocal" size="large"/>
      <span>Let your friends react to this style.</span>
      <img src="@/assets/img/icon-information-circle.svg" alt="img" @click="emits('introduce')">
    </div>

    <div class="platforms">
      <div class="line">
        <!-- x -->
        <div class="btn" v-if="shareData.shareInfo.x" @click="handleX">
          <img class="icon" src="@/assets/img/icon-x-32*32.svg" alt="x">
          <div class="name">{{ shareData.shareInfo.x.displayName }}</div>
        </div>
        <!-- facebook -->
        <div class="btn" v-if="shareData.shareInfo.facebook" @click="handleFacebook">
          <img class="icon" src="@/assets/img/icon-facebook-32*32.svg" alt="facebook">
          <div class="name">{{ shareData.shareInfo.facebook.displayName }}</div>
        </div>
        <!-- instagram -->
        <div class="btn" v-if="shareData.shareInfo.instagram" @click="handleInstagram">
          <img class="icon" src="@/assets/img/icon-instagram-32*32.svg" alt="instagram">
          <div class="name">{{ shareData.shareInfo.instagram.displayName }}</div>
        </div>
        <!-- pinterest -->
        <div class="btn" v-if="shareData.shareInfo.pinterest" @click="handlePinterest">
          <img class="icon" src="@/assets/img/icon-pinterest.svg" alt="pinterest">
          <div class="name">{{ shareData.shareInfo.pinterest.displayName }}</div>
        </div>
      </div>
      <div class="line">
        <!-- Messages -->
        <div class="btn" v-if="shareData.shareInfo.messages" @click="handleMessage">
          <img class="icon" src="@/assets/img/icon-message.svg" alt="message">
          <div class="name">{{ shareData.shareInfo.messages.displayName }}</div>
        </div>
        <!-- 电邮分享 -->
        <div class="btn" v-if="shareData.shareInfo.mail" @click="handleEmail">
          <img class="icon" src="@/assets/img/icon-email-blue.svg" alt="email">
          <div class="name">{{ shareData.shareInfo.mail.displayName }}</div>
        </div>
        <!-- 复制链接 -->
        <div class="btn" v-if="shareData.shareInfo.copyLink" @click="handleCopyLink">
          <img class="icon" src="@/assets/img/icon-copy-link-circle.svg" alt="">
          <div class="name">{{ shareData.shareInfo.copyLink.displayName }}</div>
        </div>
        <!-- 下载图片 -->
        <div class="btn" v-if="shareData.shareInfo.download" @click="handleDownloadImage">
          <img class="icon" src="@/assets/img/icon-download-image.svg" alt="">
          <div class="name">{{ shareData.shareInfo.download.displayName }}</div>
        </div>
        <!-- 更多 -->
        <div class="btn" v-if="shareData.shareInfo.more" @click="handleMore">
          <img class="icon" src="@/assets/img/icon-more.svg" alt="">
          <div class="name">{{ shareData.shareInfo.more.displayName }}</div>
        </div>
      </div>
    </div>
  </div>
  <div class="slide-product-share-desktop" v-else>
    <div class="previewImage">
      <div class="top">
        <div class="top-left"><img v-if="shareData.productInfo.mainImage.url" :src="shareData.productInfo.mainImage.url" alt="img"></div>
        <div class="top-right" v-if="isVoteLocal && !isLoading">
          <img src="@/assets/img/icon-like.png" alt="icon">
          <img src="@/assets/img/icon-half-like.png" alt="icon">
          <img src="@/assets/img/icon-unlike.png" alt="icon">
        </div>
      </div>
      <div class="bottom">
        <div class="name">{{ shareData.productInfo.productName }}</div>
        <div class="rate">
          <div class="stars">
            <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in fullStars">
            <img :src="halfStarImg" alt="star" v-if="hasHalfStar">
            <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in emptyStars">
          </div>
          <div class="points">{{ shareData.productInfo.reviewAverage }}</div>
        </div>
      </div>
    </div>
    <div class="checkbox-vote">
      <el-checkbox v-model="isVoteLocal" size="large"/>
      <span>Get reactions from friends on this item.</span>
      <img src="@/assets/img/icon-information-circle.svg" alt="img" @click="emits('introduce')">
    </div>
    <div class="platforms">
      <!-- x -->
      <div class="btn" v-if="shareData.shareInfo.x" @click="handleX">
        <img class="icon" src="@/assets/img/icon-x-32*32.svg" alt="x">
      </div>
      <!-- facebook -->
      <div class="btn" v-if="shareData.shareInfo.facebook" @click="handleFacebook">
        <img class="icon" src="@/assets/img/icon-facebook-32*32.svg" alt="facebook">
      </div>
      <!-- instagram -->
      <div class="btn" v-if="shareData.shareInfo.instagram" @click="handleInstagram">
        <img class="icon" src="@/assets/img/icon-instagram-32*32.svg" alt="instagram">
      </div>
      <!-- pinterest -->
      <div class="btn" v-if="shareData.shareInfo.pinterest" @click="handlePinterest">
        <img class="icon" src="@/assets/img/icon-pinterest.svg" alt="pinterest">
      </div>
      <!-- Messages -->
      <div class="btn" v-if="shareData.shareInfo.messages" @click="handleMessage">
        <img class="icon" src="@/assets/img/icon-message.svg" alt="message">
      </div>
      <!-- 电邮分享 -->
      <div class="btn" v-if="shareData.shareInfo.mail" @click="handleEmail">
        <img class="icon" src="@/assets/img/icon-email-blue.svg" alt="email">
      </div>
      <!-- 下载图片 -->
      <div class="btn" v-if="shareData.shareInfo.download" @click="handleDownloadImage">
        <img class="icon" src="@/assets/img/icon-download-image.svg" alt="">
      </div>
      <!-- 复制链接 -->
      <div class="btn" v-if="shareData.shareInfo.copyLink" @click="handleCopyLink">
        <img class="icon" src="@/assets/img/icon-copy-link-circle.svg" alt="">
      </div>
      <!-- 更多 -->
      <div class="btn" v-if="shareData.shareInfo.more" @click="handleMore">
        <img class="icon" src="@/assets/img/icon-more.svg" alt="">
      </div>
    </div>
    <div class="footer">
      <div class="btn-secondary" @click="handleClose">done</div>
    </div>
  </div>
</template>

<style lang="scss">
.slide-product-share-mobile {
  .el-checkbox {
    height: fit-content !important;
  }

  .el-checkbox__inner {
    width: 16px !important;
    height: 16px !important;

    &:after {
      left: 5px;
      top: 2px;
    }
  }
}

.slide-product-share-desktop {
  .el-checkbox {
    height: fit-content !important;
  }

  .el-checkbox__inner {
    width: 16px !important;
    height: 16px !important;

    &:after {
      left: 5px;
      top: 2px;
    }
  }
}
</style>

<style scoped lang="scss">
@import "src/assets/config";

.slide-product-share-mobile {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  .previewImage {
    width: 100%;
    height: 236px;
    display: flex;
    flex-direction: column;
    border: 0.5px solid $color-BNTO-beige-dark;

    .top {
      width: 100%;
      height: 172px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 48px;

      .top-left {
        height: 100%;
        display: flex;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .top-right {
        display: flex;

        img {
          width: 60px;
          height: 50px;
        }

      }
    }

    .bottom {
      padding: 0 8px;

      .name {
        font-family: "Alternate Gothic No3 D";
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px; /* 160% */
        text-transform: uppercase;
        padding-top: 5px;
      }

      .rate {
        display: flex;
        align-items: center;

        .stars {
          display: flex;

          img {
            width: 16px;
            height: 16px;
          }
        }

        .points {
          margin-left: 4px;
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */
        }
      }
    }
  }

  .checkbox-vote {
    margin-top: 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    /* Text xs/Regular */
    font-family: "TWK Lausanne";
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 18px; /* 150% */

    img {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }
  }

  .platforms {
    margin-top: 16px;
    padding-bottom: 30px;

    .line {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 8px;

      .btn {
        display: flex;
        flex-direction: column;
        align-items: center;

        img {
          width: 58px;
          height: 58px;
        }

        .name {
          margin-top: 8px;
          font-family: "TWK Lausanne";
          font-size: 10px;
          font-style: normal;
          font-weight: 300;
          line-height: 12px; /* 120% */
        }
      }
    }
  }
}

.slide-product-share-desktop {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  .previewImage {
    width: 100%;
    height: 236px;
    display: flex;
    flex-direction: column;
    border: 0.5px solid $color-BNTO-beige-dark;

    .top {
      width: 100%;
      height: 172px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 48px;

      .top-left {
        height: 100%;
        display: flex;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .top-right {
        display: flex;

        img {
          width: 60px;
          height: 50px;
        }

      }
    }

    .bottom {
      padding: 0 8px;

      .name {
        font-family: "Alternate Gothic No3 D";
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px; /* 160% */
        text-transform: uppercase;
        padding-top: 5px;
      }

      .rate {
        display: flex;
        align-items: center;

        .stars {
          display: flex;

          img {
            width: 16px;
            height: 16px;
          }
        }

        .points {
          margin-left: 4px;
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */
        }
      }
    }
  }

  .checkbox-vote {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    /* Text xs/Regular */
    font-family: "TWK Lausanne";
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 18px; /* 150% */

    img {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }
  }

  .platforms {
    margin-top: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .btn {
      cursor: pointer;
      display: flex;

      img {
        width: 32px;
        height: 32px;
      }
    }
  }

  .footer {
    width: 100%;
    position: absolute;
    bottom: 30px;
    padding: 0 16px;
  }
}
</style>
